import { FocusMonitor } from '@angular/cdk/a11y';
import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, fromEvent, tap } from 'rxjs';

import { IconModule, SvgIconName } from '@aw/shared/components/icon';
import { RippleDirective } from '@aw/shared/directives';
import { NavSegments } from '@aw/shared/models';

import { ButtonSpinnerComponent } from './components/button-spinner/button-spinner.component';
import {
  BUTTON_BUSINESS_UNIT,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from './constants/button.const';
import {
  ButtonBusinessUnit,
  ButtonSize,
  ButtonTextAlign,
  ButtonType,
  ButtonVariant,
} from './types/types';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'po-button',
  templateUrl: 'button.component.html',
  styleUrls: ['./button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgTemplateOutlet,
    NgClass,
    RouterLink,
    ButtonSpinnerComponent,
    RippleDirective,
    IconModule,
  ],
})
export class ButtonComponent {
  @HostBinding('class.po-button')
  readonly identifierClass = true;

  @Input() type: ButtonType = 'button';

  @Input() variant: ButtonVariant = 'primary';

  @Input() size: ButtonSize = 'medium';

  @Input() path?: NavSegments;

  @Input() queryParams?: Record<string, string | number>;

  @Input({ transform: booleanAttribute }) replaceUrl = false;

  @Input() href?: string;

  @Input() textAlign: ButtonTextAlign = 'center';

  @Input({ transform: booleanAttribute }) disabled = false;

  @Input({ transform: booleanAttribute }) loading = false;

  // If this input is provided, the ng-content will be ignored
  @Input({ required: false }) icon?: SvgIconName;

  @Input({ required: false }) prefixIcon?: SvgIconName;

  @Input({ required: false }) suffixIcon?: SvgIconName;

  @HostBinding('attr.data-business-unit')
  @Input({ required: false })
  businessUnit: ButtonBusinessUnit = 'corporate';

  @Output() readonly clicked = new EventEmitter<MouseEvent | TouchEvent>();

  readonly BUTTON_VARIANT = BUTTON_VARIANT;

  readonly BUTTON_SIZE = BUTTON_SIZE;

  readonly BUTTON_BUSINESS_UNIT = BUTTON_BUSINESS_UNIT;

  constructor() {
    this.initHostClickInterceptor();
  }

  @ViewChild('nativeBtn', { static: true })
  private readonly nativeBtn?: ElementRef<HTMLButtonElement>;

  private readonly focusMonitor = inject(FocusMonitor);

  focus(): void {
    if (this.nativeBtn) {
      this.focusMonitor.focusVia(this.nativeBtn?.nativeElement, 'keyboard');
    }
  }

  private initHostClickInterceptor(): void {
    const elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    const { nativeElement: target } = elementRef;

    fromEvent(target, 'click', { capture: true })
      .pipe(
        untilDestroyed(this),
        filter(() => this.disabled || this.loading),
        tap((event) => {
          event.stopPropagation();
          event.preventDefault();
        }),
      )
      .subscribe();
  }
}
